import { CheckBoxWithLabelProps, CheckBoxWithLabelWidget, InputAttrs, TabStyles } from '@tableau/widgets';
import { useField } from 'formik';
import React, {ReactNode} from 'react';
import { style } from 'typestyle';
import _ from 'underscore';

export type FormikCheckboxProps = {
  name: string;
  label: string;
  testId: string;
  className?: string;
  beforeChange?: (event: React.ChangeEvent<HTMLInputElement>) => Promise<any>;
  alwaysShowChildren?: boolean;
  childrenSkipIndent?: boolean;
  // The following prop is passed directly to the underlying CheckBoxWithLabelWidget, see http://design/widgets/widget-library/check-box/check-box-with-label
  checkboxWithLabelChildren?: ReactNode;
}

const indentedPadding = style({
  paddingTop: 2 * TabStyles.Spacing.SmallSpace,
  paddingLeft: 20,
});

/**
 * A checkbox using {@link CheckBoxWithLabelWidget} which automatically wires to a boolean formik field using the
 * {@code name} prop. Nested child components are automatically hidden when the checkbox is unchecked.
 */

export const FormikCheckbox: React.FunctionComponent<FormikCheckboxProps & Partial<CheckBoxWithLabelProps> & InputAttrs> =
function FormikCheckbox(props) {
  const [ field ] = useField({name: props.name, type: 'checkbox'});
  // spread the props last, which will let callers override checked and onChange by providing them as props
  const { beforeChange, alwaysShowChildren, childrenSkipIndent, checked, children, checkboxWithLabelChildren, ...otherProps } = props;

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // For some reason using event.persist() wasn't returning the right value
      // React's synthetic event requires the key's be explicitly referenced.
      const originalTarget = _.pick(event.target, 'name', 'type', 'checked', 'value');
      if (beforeChange) {
        beforeChange(event).then(() => {
          field.onChange({ target: originalTarget });
        }).catch(() => { /* no-op for now */ });
      } else {
        field.onChange(event);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [beforeChange, field?.onChange]
  );

  return (
    <>
      <CheckBoxWithLabelWidget
        checked={props.checked ?? field.checked}
        onChange={handleChange}
        children={checkboxWithLabelChildren}
        removeLeftMargin
        {...otherProps}
        // need to set value to the string 'true' or 'false' in order for formik to behave when initial value is undefined
        // this is probably a bug in formik that needs to be addressed.
        // Also take props.checked into account to override the formik value if needed
        value={(props.checked ?? field.checked) ? 'true' : 'false'}
      />
      {
        (props.alwaysShowChildren || (props.checked ?? field.checked)) && children &&
          <div className={props.childrenSkipIndent ? '' : indentedPadding}>
            {children}
          </div>
      }
    </>
  );
};
